import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Trash2, Loader2, Pencil } from "lucide-react";
import { hexToRgba, ColorPicker } from "../../components/ColorPicker";

const STATUS_STYLES = {
  "not started": "status-not-started",
  "open": "status-open",
  "waiting": "status-waiting",
  "closed": "status-closed",
};

const DEFAULT_COLOR = "#3B82F6";

export function ProjectHeader({ project, user, t, s, onSaveProject, handleDeleteProject }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState(project.title || "");
  const [description, setDescription] = useState(project.description || "");
  const [status, setStatus] = useState(project.status || "not started");
  const [color, setColor] = useState(project.color || DEFAULT_COLOR);

  const accent = project.color || DEFAULT_COLOR;

  const openDialog = () => {
    // Seed from the project each time, so an abandoned edit leaves nothing
    // behind for the next one.
    setTitle(project.title || "");
    setDescription(project.description || "");
    setStatus(project.status || "not started");
    setColor(project.color || DEFAULT_COLOR);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSaveProject({
        title: title.trim(),
        description,
        status,
        color,
      });
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  // Every status goes through a translation. Only "waiting" used to, so a
  // project could show its status in Italian on one page and English here.
  const statusLabel = (value) => {
    if (value === "waiting") return s ? s("proj.statusWaiting") : "Waiting";
    if (value === "not started") return t("status.notStarted");
    if (value === "open") return t("status.open");
    if (value === "closed") return t("status.closed");
    return value;
  };

  return (
    <div
      className="rounded-2xl border p-5 lg:p-6 mb-6"
      style={{
        // A wash of the project's own colour rather than a block of it: enough
        // to make the page recognisably this project, light enough that the
        // text on top needs no adjusting.
        background: `linear-gradient(135deg, ${hexToRgba(accent, 0.16)}, ${hexToRgba(accent, 0.04)})`,
        borderColor: hexToRgba(accent, 0.28),
      }}
    >
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-3">
            <span
              className={`tag ${STATUS_STYLES[project.status] || STATUS_STYLES["not started"]}`}
              data-testid="project-status"
            >
              {statusLabel(project.status)}
            </span>
          </div>

          {/* break-words: a title can be one unbroken string \u2014 a part number,
              a URL \u2014 and at this size it would push the whole card past the
              edge of a phone. */}
          <h1
            className="text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 mb-2 break-words"
            data-testid="project-title"
          >
            {project.title}
          </h1>

          <p className="text-slate-600 max-w-2xl break-words" data-testid="project-description">
            {project.description || t("project.noDescription")}
          </p>
        </div>

        {/* Controls sit on white so they stay legible whatever the tint. Edit
            takes the width it needs on a phone, where it is stacked under the
            title rather than beside it. */}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={openDialog}
            data-testid="edit-project-btn"
            className="border-slate-200 bg-white/90 gap-2 flex-1 lg:flex-none"
          >
            <Pencil className="w-4 h-4" />
            {s ? s("common.editShort") : "Edit"}
          </Button>

          {user?.role === "admin" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="delete-project-btn"
                  aria-label={t("project.deleteProject")}
                  className="border-red-200 text-red-600 hover:bg-red-50 bg-white/90 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("project.deleteProject")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("project.deleteProjectDesc")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-slate-200">{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteProject} data-testid="confirm-delete-project" className="bg-red-600 hover:bg-red-700">{t("common.delete")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Everything set at creation is editable here, in one place, rather
          than title and description inline and the rest scattered. */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{s ? s("proj.editTitle") : "Edit project"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="editTitle">{t("projects.create.name")}</Label>
              <Input
                id="editTitle"
                data-testid="edit-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editDescription">{t("projects.create.description")}</Label>
              <Textarea
                id="editDescription"
                data-testid="edit-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="border-slate-200 resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editStatus">{t("projects.create.status")}</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="edit-status-select" className="border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not started">{t("status.notStarted")}</SelectItem>
                  <SelectItem value="open">{t("status.open")}</SelectItem>
                  <SelectItem value="waiting">{s ? s("proj.statusWaiting") : "Waiting"}</SelectItem>
                  <SelectItem value="closed">{t("status.closed")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ColorPicker label={s ? s("proj.color") : "Colour"} value={color} onChange={setColor} />
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-slate-200">
                {t("common.cancel")}
              </Button>
              <Button type="submit" data-testid="save-project-btn" disabled={saving} className="bg-indigo-700 hover:bg-indigo-600 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
